<template>
  <div class="w-full font-sans text-slate-800 dark:text-[#F1F5F9] antialiased">
    <div class="max-w-7xl mx-auto">
      <!-- Header Section -->
      <div
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
      >
        <div>
          <div class="flex items-center gap-3">
            <div
              class="w-11 h-11 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-lg shadow-blue-500/20"
            >
              <Cpu class="w-5 h-5" />
            </div>
            <div>
              <div class="flex items-center gap-2.5">
                <h2
                  class="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-[#F1F5F9]"
                >
                  Hardware Terminals & Readers
                </h2>
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200/60 dark:border-blue-800/40"
                >
                  {{ devices.length }} Installed
                </span>
              </div>
              <p class="text-xs sm:text-sm text-slate-500 dark:text-[#A6B4C8] mt-0.5">
                Monitor live RFID/NFC scanning terminals, pairing codes, and status
              </p>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex items-center gap-2.5 w-full sm:w-auto">
          <button
            @click="handleRefreshDevices(false)"
            :disabled="isDevicesLoading"
            type="button"
            class="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white dark:bg-[#111C30] hover:bg-slate-50 dark:hover:bg-[#18243A] active:scale-[0.98] border border-slate-200 dark:border-[#263449] text-slate-700 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-white text-xs sm:text-sm font-semibold shadow-sm transition disabled:opacity-50"
          >
            <RefreshCw
              v-if="!isDevicesLoading"
              class="w-4 h-4 text-slate-500 dark:text-[#7E8DA5]"
            />
            <AppLoader
              v-else
              variant="button"
              class="w-4 h-4 text-indigo-600 dark:text-cyan-400"
            />
            <span>{{ isDevicesLoading ? "Refreshing..." : "Refresh" }}</span>
          </button>

          <button
            @click="isModalOpen = true"
            type="button"
            class="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white text-xs sm:text-sm font-semibold shadow-sm shadow-indigo-600/20 hover:shadow-md transition"
          >
            <Plus class="w-4 h-4" />
            <span>Book New Terminal</span>
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div
        v-if="isDevicesLoading && devices.length === 0"
        class="py-16 bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200 dark:border-[#263449] shadow-sm flex items-center justify-center"
      >
        <AppLoader
          variant="card"
          size="md"
          message="Loading Hardware Terminals..."
          subtext="Detecting NFC readers, biometrics, and scanning stations"
          color="indigo"
        />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="devices.length === 0"
        class="text-center py-16 bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200 dark:border-[#263449] shadow-sm"
      >
        <div
          class="w-14 h-14 rounded-2xl bg-slate-50 dark:bg-[#18243A] text-slate-400 dark:text-[#7E8DA5] flex items-center justify-center mx-auto mb-3"
        >
          <Cpu class="w-7 h-7" />
        </div>
        <h3 class="text-base font-bold text-slate-900 dark:text-[#F1F5F9] mb-1">
          No Terminals Configured Yet
        </h3>
        <p class="text-xs text-slate-400 dark:text-[#7E8DA5] max-w-sm mx-auto mb-5">
          Order your first smart NFC/RFID attendance terminal to begin automated scanning.
        </p>
        <button
          @click="isModalOpen = true"
          type="button"
          class="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white rounded-xl font-semibold text-xs sm:text-sm shadow-sm shadow-indigo-600/20 transition"
        >
          <Plus class="w-4 h-4" />
          <span>Book Terminal Now</span>
        </button>
      </div>

      <!-- Device Cards Grid -->
      <div
        v-else
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
      >
        <div
          v-for="device in devices"
          :key="device._id"
          class="group bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200 dark:border-[#263449] hover:border-blue-500/50 dark:hover:border-blue-500/50 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col justify-between overflow-hidden"
        >
          <div class="p-4 sm:p-5">
            <!-- Top Header Row -->
            <div class="flex items-start justify-between gap-3 mb-4">
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <span
                    :class="[
                      'w-2 h-2 rounded-full flex-shrink-0',
                      device.status === 'active'
                        ? 'bg-emerald-500 animate-pulse'
                        : device.status === 'disabled'
                        ? 'bg-rose-500'
                        : 'bg-amber-500',
                    ]"
                  ></span>
                  <h3
                    class="text-base font-bold text-slate-900 dark:text-[#F1F5F9] truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors"
                  >
                    {{ device.label ? device.label : device.deviceName }}
                  </h3>
                </div>

                <div
                  class="flex items-center gap-2 text-xs text-slate-400 dark:text-[#7E8DA5] mt-1 pl-4"
                >
                  <span
                    v-if="device.label"
                    class="truncate font-medium text-slate-600 dark:text-[#A6B4C8]"
                  >
                    {{ device.deviceName }}
                  </span>
                  <span
                    v-if="device.label"
                    class="text-slate-300 dark:text-slate-600"
                    >•</span
                  >
                  <span class="font-mono text-[11px]">ID: {{ device.chipId || "N/A" }}</span>
                </div>
              </div>

              <!-- Menu Dropdown -->
              <div class="relative flex-shrink-0">
                <button
                  @click="toggleMenu(device._id)"
                  type="button"
                  class="p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#18243A] transition"
                  title="Device Options"
                >
                  <MoreVertical class="w-4 h-4" />
                </button>

                <transition name="fade-scale">
                  <div
                    v-if="openMenus[device._id]"
                    class="absolute right-0 mt-1.5 w-52 bg-white dark:bg-[#18243A] rounded-2xl shadow-xl border border-slate-200 dark:border-[#263449] py-1.5 z-30"
                    @click.stop
                  >
                    <button
                      @click="
                        handleGenerateNewCode(device);
                        openMenus[device._id] = false;
                      "
                      :disabled="generatingCodeId === device._id"
                      type="button"
                      class="w-full text-left px-3.5 py-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 disabled:opacity-50 transition flex items-center gap-2 text-xs font-semibold"
                    >
                      <RefreshCw
                        :class="[
                          'w-3.5 h-3.5',
                          generatingCodeId === device._id ? 'animate-spin' : '',
                        ]"
                      />
                      <span>
                        {{
                          generatingCodeId === device._id
                            ? "Generating..."
                            : "Generate Setup Code"
                        }}
                      </span>
                    </button>

                    <button
                      @click="
                        openEditModal(device);
                        openMenus[device._id] = false;
                      "
                      type="button"
                      class="w-full text-left px-3.5 py-2 text-slate-700 dark:text-[#F1F5F9] hover:bg-slate-50 dark:hover:bg-[#172235] transition flex items-center gap-2 text-xs font-semibold"
                    >
                      <Edit class="w-3.5 h-3.5 text-slate-400" />
                      <span>Edit Device Details</span>
                    </button>

                    <div class="my-1 border-t border-slate-100 dark:border-[#263449]"></div>

                    <button
                      @click="
                        openFactoryResetWarning(device);
                        openMenus[device._id] = false;
                      "
                      type="button"
                      class="w-full text-left px-3.5 py-2 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition flex items-center gap-2 text-xs font-semibold"
                    >
                      <RotateCcw class="w-3.5 h-3.5" />
                      <span>Factory Reset</span>
                    </button>
                  </div>
                </transition>
              </div>
            </div>

            <!-- Setup Code / Last Setup Section -->
            <div
              class="rounded-xl p-3.5 bg-slate-50 dark:bg-[#172235] border border-slate-200/80 dark:border-[#263449] mb-4"
            >
              <!-- If Code Exists -->
              <template v-if="device.code && !isCodeExpired(device)">
                <div class="flex items-center justify-between mb-2">
                  <span
                    class="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider"
                  >
                    Pairing Setup Code
                  </span>
                  <span
                    class="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 dark:text-amber-400 bg-amber-100/80 dark:bg-amber-950/40 px-2 py-0.5 rounded-full"
                  >
                    <span
                      class="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"
                    ></span>
                    {{ getRemainingTime(device.setupCodeExpiresAt) }}
                  </span>
                </div>

                <!-- Code Clickable Box -->
                <div
                  @click="copyToClipboard(device)"
                  class="group/code flex items-center justify-between bg-white dark:bg-[#111C30] rounded-xl px-3.5 py-2.5 border border-slate-200 dark:border-[#263449] hover:border-blue-500 dark:hover:border-blue-500 cursor-pointer transition shadow-sm"
                  title="Click to copy pairing code"
                >
                  <code
                    class="font-mono font-extrabold text-lg sm:text-xl tracking-[0.2em] text-slate-900 dark:text-[#F1F5F9] group-hover/code:text-blue-600 dark:group-hover/code:text-blue-400 transition"
                  >
                    {{ device.code }}
                  </code>
                  <div
                    class="p-1 rounded-lg text-slate-400 dark:text-[#7E8DA5] group-hover/code:text-blue-600"
                  >
                    <Copy
                      v-if="!copiedMap[device._id]"
                      class="w-4 h-4"
                    />
                    <Check
                      v-else
                      class="w-4 h-4 text-emerald-600 dark:text-emerald-400"
                    />
                  </div>
                </div>
                <p class="text-[11px] text-slate-400 dark:text-[#7E8DA5] mt-2">
                  Enter this code on the hardware terminal keypad to pair.
                </p>
              </template>

              <!-- If Installed / No Active Code -->
              <template v-else>
                <div class="flex items-center justify-between mb-1.5">
                  <span
                    class="text-[10px] font-bold text-slate-500 dark:text-[#7E8DA5] uppercase tracking-wider"
                  >
                    Pairing Status
                  </span>
                  <span
                    v-if="device.lastSetupAt"
                    class="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-100/80 dark:bg-emerald-950/40 px-2 py-0.5 rounded-full"
                  >
                    Online & Paired
                  </span>
                  <span
                    v-else
                    class="text-[11px] font-semibold text-amber-700 dark:text-amber-400 bg-amber-100/80 dark:bg-amber-950/40 px-2 py-0.5 rounded-full"
                  >
                    Pending Setup
                  </span>
                </div>

                <p class="text-xs sm:text-sm font-semibold text-slate-800 dark:text-[#F1F5F9]">
                  {{
                    device.lastSetupAt
                      ? new Date(device.lastSetupAt).toLocaleString(undefined, {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })
                      : "No active pairing yet"
                  }}
                </p>
                <p class="text-[11px] text-slate-400 dark:text-[#7E8DA5] mt-1">
                  {{
                    device.lastSetupAt
                      ? formatRelativeTime(device.lastSetupAt)
                      : "Use menu to generate a pairing setup code."
                  }}
                </p>
              </template>
            </div>

            <!-- Attributes Grid -->
            <div class="grid grid-cols-2 gap-2.5">
              <div class="bg-slate-50 dark:bg-[#172235] rounded-xl p-2.5 border border-slate-200/60 dark:border-[#263449]">
                <span
                  class="block text-[10px] font-bold text-slate-400 dark:text-[#7E8DA5] uppercase tracking-wider"
                  >Device Protocol</span
                >
                <span class="text-xs font-semibold text-slate-700 dark:text-[#F1F5F9]"
                  >NFC 13.56MHz</span
                >
              </div>

              <div
                class="bg-slate-50 dark:bg-[#172235] rounded-xl p-2.5 border border-slate-200/60 dark:border-[#263449] flex flex-col justify-center"
              >
                <span
                  class="block text-[10px] font-bold text-slate-400 dark:text-[#7E8DA5] uppercase tracking-wider"
                  >Gateway Status</span
                >
                <span
                  :class="[
                    'text-xs font-bold capitalize',
                    device.status === 'active'
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : device.status === 'disabled'
                      ? 'text-rose-600 dark:text-rose-400'
                      : 'text-amber-600 dark:text-amber-400',
                  ]"
                >
                  {{ device.status || "Pending" }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Book Device Modal -->
    <div
      v-if="isModalOpen"
      class="fixed inset-0 bg-slate-900/60 dark:bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      @click.self="isModalOpen = false"
    >
      <div
        class="bg-white dark:bg-[#111C30] rounded-2xl shadow-2xl max-w-md w-full p-6 border border-slate-200 dark:border-[#263449]"
      >
        <div class="flex items-center justify-between mb-5">
          <h2 class="text-lg sm:text-xl font-bold text-slate-900 dark:text-[#F1F5F9]">
            Book Hardware Readers
          </h2>
          <button
            @click="isModalOpen = false"
            type="button"
            class="p-1 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#18243A] transition"
          >
            <X class="w-5 h-5" />
          </button>
        </div>

        <div
          v-if="bookingSuccess"
          class="mb-4 p-3 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/40 text-emerald-700 dark:text-emerald-400 text-xs font-semibold rounded-xl flex items-center gap-2"
        >
          <CheckCircle class="w-4 h-4 text-emerald-600 flex-shrink-0" />
          <span>Hardware devices booked successfully!</span>
        </div>

        <div
          v-if="bookingError"
          class="mb-4 p-3 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/40 text-rose-700 dark:text-rose-400 text-xs font-semibold rounded-xl flex items-center gap-2"
        >
          <AlertCircle class="w-4 h-4 text-rose-600 flex-shrink-0" />
          <span>{{ bookingError }}</span>
        </div>

        <form
          @submit.prevent="handleBookDevices"
          class="space-y-4"
        >
          <div>
            <label
              class="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-[#A6B4C8] mb-1.5"
            >
              Number of Terminals to Order
            </label>
            <input
              v-model.number="bookForm.numberOfDevices"
              type="number"
              min="1"
              max="50"
              placeholder="e.g., 2"
              required
              class="w-full px-4 py-2.5 bg-slate-50 dark:bg-[#172235] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-800 dark:text-[#F1F5F9] text-sm placeholder-slate-400 dark:placeholder-[#7E8DA5] focus:outline-none focus:bg-white dark:focus:bg-[#18243A] focus:border-blue-600 focus:ring-4 focus:ring-blue-500/10 transition"
            />
            <p class="text-[11px] text-slate-400 dark:text-[#7E8DA5] mt-1">
              Each unit includes NFC/RFID scanner module, mounting kit, and power supply.
            </p>
          </div>

          <button
            type="submit"
            :disabled="isBookingLoading"
            class="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] disabled:opacity-60 disabled:pointer-events-none text-white font-semibold text-sm rounded-xl transition shadow-sm shadow-indigo-600/20"
          >
            {{ isBookingLoading ? "Processing Booking..." : "Confirm Terminal Order" }}
          </button>
        </form>

        <button
          @click="isModalOpen = false"
          type="button"
          class="w-full mt-2.5 py-2.5 px-4 text-slate-600 dark:text-[#A6B4C8] hover:bg-slate-100 dark:hover:bg-[#18243A] font-semibold text-xs sm:text-sm rounded-xl transition"
        >
          Cancel
        </button>
      </div>
    </div>

    <!-- Edit Device Modal -->
    <div
      v-if="isEditModalOpen"
      class="fixed inset-0 bg-slate-900/60 dark:bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      @click.self="isEditModalOpen = false"
    >
      <div
        class="bg-white dark:bg-[#111C30] rounded-2xl shadow-2xl max-w-md w-full p-6 border border-slate-200 dark:border-[#263449]"
      >
        <div class="flex items-center justify-between mb-5">
          <h2 class="text-lg sm:text-xl font-bold text-slate-900 dark:text-[#F1F5F9]">
            Edit Terminal Details
          </h2>
          <button
            @click="isEditModalOpen = false"
            type="button"
            class="p-1 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#18243A] transition"
          >
            <X class="w-5 h-5" />
          </button>
        </div>

        <div
          v-if="editError"
          class="mb-4 p-3 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/40 text-rose-700 dark:text-rose-400 text-xs font-semibold rounded-xl flex items-center gap-2"
        >
          <AlertCircle class="w-4 h-4 text-rose-600 flex-shrink-0" />
          <span>{{ editError }}</span>
        </div>

        <div
          v-if="editSuccess"
          class="mb-4 p-3 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/40 text-emerald-700 dark:text-emerald-400 text-xs font-semibold rounded-xl flex items-center gap-2"
        >
          <CheckCircle class="w-4 h-4 text-emerald-600 flex-shrink-0" />
          <span>Device details updated successfully!</span>
        </div>

        <form
          @submit.prevent="handleEditDevice"
          class="space-y-4"
        >
          <div>
            <label
              class="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-[#A6B4C8] mb-1.5"
            >
              Device Name
            </label>
            <input
              v-model="editForm.deviceName"
              type="text"
              placeholder="e.g., Main Gate Reader 1"
              required
              class="w-full px-4 py-2.5 bg-slate-50 dark:bg-[#172235] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-800 dark:text-[#F1F5F9] text-sm placeholder-slate-400 dark:placeholder-[#7E8DA5] focus:outline-none focus:bg-white dark:focus:bg-[#18243A] focus:border-blue-600 focus:ring-4 focus:ring-blue-500/10 transition"
            />
          </div>

          <div>
            <label
              class="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-[#A6B4C8] mb-1.5"
            >
              Physical Location / Label
            </label>
            <input
              v-model="editForm.label"
              type="text"
              placeholder="e.g., Building A Entrance Turnstile"
              class="w-full px-4 py-2.5 bg-slate-50 dark:bg-[#172235] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-800 dark:text-[#F1F5F9] text-sm placeholder-slate-400 dark:placeholder-[#7E8DA5] focus:outline-none focus:bg-white dark:focus:bg-[#18243A] focus:border-blue-600 focus:ring-4 focus:ring-blue-500/10 transition"
            />
          </div>

          <button
            type="submit"
            :disabled="isEditLoading"
            class="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] disabled:opacity-60 disabled:pointer-events-none text-white font-semibold text-sm rounded-xl transition shadow-sm shadow-indigo-600/20"
          >
            {{ isEditLoading ? "Saving Changes..." : "Save Changes" }}
          </button>
        </form>

        <button
          @click="isEditModalOpen = false"
          type="button"
          class="w-full mt-2.5 py-2.5 px-4 text-slate-600 dark:text-[#A6B4C8] hover:bg-slate-100 dark:hover:bg-[#18243A] font-semibold text-xs sm:text-sm rounded-xl transition"
        >
          Cancel
        </button>
      </div>
    </div>

    <!-- Factory Reset Warning Modal -->
    <div
      v-if="isFactoryResetWarningOpen"
      class="fixed inset-0 bg-slate-900/60 dark:bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      @click.self="isFactoryResetWarningOpen = false"
    >
      <div
        class="bg-white dark:bg-[#111C30] rounded-2xl shadow-2xl max-w-md w-full p-6 border border-slate-200 dark:border-[#263449]"
      >
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-3">
            <div class="p-2.5 bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 rounded-xl">
              <RotateCcw class="w-5 h-5" />
            </div>
            <h2 class="text-lg sm:text-xl font-bold text-slate-900 dark:text-[#F1F5F9]">
              Factory Reset Terminal
            </h2>
          </div>
          <button
            @click="isFactoryResetWarningOpen = false"
            type="button"
            class="p-1 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#18243A] transition"
          >
            <X class="w-5 h-5" />
          </button>
        </div>

        <div
          class="mb-4 p-3.5 bg-rose-50 dark:bg-rose-950/30 border border-rose-200/80 dark:border-rose-900/40 rounded-xl"
        >
          <h3
            class="text-xs font-bold text-rose-900 dark:text-rose-300 uppercase tracking-wide mb-1"
          >
            Irreversible Action
          </h3>
          <p class="text-rose-800 dark:text-rose-200 text-xs leading-relaxed">
            This will purge Wi-Fi credentials, offline scan queues, and encryption keys from the terminal hardware.
          </p>
        </div>

        <div
          class="mb-5 p-3.5 bg-slate-50 dark:bg-[#172235] rounded-xl border border-slate-200 dark:border-[#263449]"
        >
          <span
            class="block text-[10px] font-bold text-slate-400 dark:text-[#7E8DA5] uppercase tracking-wider"
          >
            Target Terminal
          </span>
          <p class="text-sm font-bold text-slate-900 dark:text-[#F1F5F9] mt-0.5">
            {{ selectedDeviceForReset?.deviceName }}
          </p>
          <p class="text-xs text-slate-500 dark:text-[#7E8DA5] font-mono mt-0.5">
            Chip ID: {{ selectedDeviceForReset?.chipId || "N/A" }}
          </p>
        </div>

        <div
          v-if="factoryResetError"
          class="mb-4 p-3 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/40 text-rose-700 dark:text-rose-400 text-xs font-semibold rounded-xl flex items-start gap-2"
        >
          <AlertCircle class="w-4 h-4 mt-0.5 flex-shrink-0 text-rose-600" />
          <span>{{ factoryResetError }}</span>
        </div>

        <div class="flex gap-2.5 flex-col sm:flex-row">
          <button
            @click="isFactoryResetWarningOpen = false"
            type="button"
            class="flex-1 py-2.5 px-4 bg-slate-100 dark:bg-[#18243A] hover:bg-slate-200 dark:hover:bg-[#172235] text-slate-700 dark:text-[#A6B4C8] font-semibold text-xs sm:text-sm rounded-xl transition"
          >
            Cancel
          </button>
          <button
            @click="handleFactoryReset"
            :disabled="
              isFactoryResetLoading ||
              selectedDeviceForReset?.status !== 'active'
            "
            type="button"
            class="flex-1 py-2.5 px-4 bg-rose-600 hover:bg-rose-700 active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none text-white font-semibold text-xs sm:text-sm rounded-xl transition shadow-sm"
          >
            {{
              isFactoryResetLoading
                ? "Resetting..."
                : selectedDeviceForReset?.status !== "active"
                ? "Active Only"
                : "Confirm Reset"
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import {
    ref,
    computed,
    onMounted,
    onUnmounted,
    onActivated,
    onDeactivated,
  } from "vue";
  import { useStore } from "vuex";
  import { useGlobalToast } from "@/utils/useGlobalToast";
  import {
    Cpu,
    Plus,
    RefreshCw,
    MoreVertical,
    Copy,
    Check,
    Edit,
    RotateCcw,
    X,
    CheckCircle,
    AlertCircle,
  } from "lucide-vue-next";

  const { showToast } = useGlobalToast();
  const store = useStore();

  const isModalOpen = ref(false);
  const openMenus = ref({});
  const now = ref(Date.now());
  let timerInterval = null;
  let autoRefreshInterval = null;
  const bookForm = ref({
    schoolName: "",
    numberOfDevices: "",
    numberOfStudents: "",
  });

  const isBookingLoading = ref(false);
  const bookingSuccess = ref(false);
  const bookingError = ref("");

  const isDevicesLoading = ref(false);
  const copiedMap = ref({});
  const generatingCodeId = ref(null);

  const isFactoryResetWarningOpen = ref(false);
  const isFactoryResetLoading = ref(false);
  const factoryResetError = ref("");
  const selectedDeviceForReset = ref(null);
  let pairingInterval = null;

  const isEditModalOpen = ref(false);
  const isEditLoading = ref(false);
  const editError = ref("");
  const editSuccess = ref(false);
  const selectedDeviceForEdit = ref(null);
  const editForm = ref({
    deviceName: "",
    label: "",
  });

  const devices = computed(() => store.state.devices || []);

  const toggleMenu = (deviceId) => {
    openMenus.value[deviceId] = !openMenus.value[deviceId];
  };

  const copyToClipboard = async (device) => {
    try {
      await navigator.clipboard.writeText(device.code);

      copiedMap.value[device._id] = true;

      setTimeout(() => {
        delete copiedMap.value[device._id];
      }, 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const openEditModal = (device) => {
    selectedDeviceForEdit.value = device;
    editForm.value = {
      deviceName: device.deviceName || "",
      label: device.label || "",
    };
    editError.value = "";
    editSuccess.value = false;
    isEditModalOpen.value = true;
  };

  const handleEditDevice = async () => {
    if (!selectedDeviceForEdit.value) return;

    editError.value = "";
    editSuccess.value = false;

    if (!editForm.value.deviceName.trim()) {
      editError.value = "Device name is required";
      return;
    }

    isEditLoading.value = true;

    try {
      await store.dispatch("updateDeviceDetails", {
        deviceId: selectedDeviceForEdit.value._id,
        deviceName: editForm.value.deviceName.trim(),
        label: editForm.value.label.trim() || undefined,
      });

      editSuccess.value = true;
      showToast("success", "Device updated successfully!");

      setTimeout(() => {
        isEditModalOpen.value = false;
        editSuccess.value = false;
      }, 1500);

      await handleRefreshDevices();
    } catch (error) {
      console.error("Failed to update device:", error);
      editError.value =
        error.response?.data?.error ||
        error.message ||
        "Failed to update device. Please try again.";
      showToast("error", editError.value);
    } finally {
      isEditLoading.value = false;
    }
  };

  const openFactoryResetWarning = (device) => {
    selectedDeviceForReset.value = device;
    factoryResetError.value = "";
    isFactoryResetWarningOpen.value = true;
  };

  const handleGenerateNewCode = async (device) => {
    if (!device || !device._id) return;

    generatingCodeId.value = device._id;

    try {
      await store.dispatch("generateDeviceSetupCode", device._id);

      await handleRefreshDevices();
      showToast("success", "New setup code generated successfully!");
      startPairingWatcher(device._id);
    } catch (error) {
      console.error("Failed to generate new code:", error);

      showToast(
        "error",
        error?.response?.data?.error || "Failed to generate new code",
      );
    } finally {
      generatingCodeId.value = null;
    }
  };

  const handleFactoryReset = async () => {
    if (!selectedDeviceForReset.value) return;

    factoryResetError.value = "";
    isFactoryResetLoading.value = true;

    try {
      await store.dispatch("factoryReset", {
        deviceId: selectedDeviceForReset.value._id,
      });

      showToast(
        "success",
        "Reset command sent. Device will reset within a few seconds.",
      );

      isFactoryResetWarningOpen.value = false;

      const watchId = selectedDeviceForReset.value._id;

      const waitForDeviceReset = setInterval(async () => {
        await handleRefreshDevices(true);

        const updated = devices.value.find((d) => d._id === watchId);

        if (!updated || updated.status === "pending") {
          clearInterval(waitForDeviceReset);
          showToast("success", "Device reset completed and ready for setup.");
        }
      }, 3000);
    } catch (error) {
      console.error("❌ Failed factory reset:", error);
      factoryResetError.value =
        error.message ||
        "Failed to reset device. Please try again or contact support.";
    } finally {
      isFactoryResetLoading.value = false;
    }
  };

  const handleBookDevices = async () => {
    bookingError.value = "";
    bookingSuccess.value = false;

    if (!bookForm.value.numberOfDevices) {
      bookingError.value = "Please enter number of devices";
      return;
    }

    isBookingLoading.value = true;

    try {
      await store.dispatch("bookDevices", {
        numberOfDevices: bookForm.value.numberOfDevices,
      });

      bookingSuccess.value = true;
      bookForm.value = {
        numberOfDevices: "",
      };

      setTimeout(() => {
        bookingSuccess.value = false;
        isModalOpen.value = false;
      }, 2000);
      showToast("success", "Devices booked successfully!");

      await handleRefreshDevices();
    } catch (error) {
      bookingError.value = error.message || "Failed to book devices";
      showToast("error", bookingError.value);
    } finally {
      isBookingLoading.value = false;
    }
  };

  const handleRefreshDevices = async (silent = false) => {
    if (!silent) {
      isDevicesLoading.value = true;
    }

    try {
      const res = await store.dispatch("fetchMyDevices");

      const anyMenuOpen = Object.values(openMenus.value).some(Boolean);

      if (!anyMenuOpen) {
        const deviceWithoutCode = devices.value.find((d) => !d.code);
        if (deviceWithoutCode) {
          openMenus.value[deviceWithoutCode._id] = true;
        }
      }

      if (res?.serverTime) {
        now.value = new Date(res.serverTime).getTime();
      } else {
        now.value = Date.now();
      }
    } catch (error) {
      console.error("Failed to fetch devices:", error);
    } finally {
      if (!silent) {
        isDevicesLoading.value = false;
      }
    }
  };

  const getRemainingTime = (expiry) => {
    if (!expiry) return null;

    const diff = new Date(expiry).getTime() - now.value;
    if (diff <= 0) return "Expired";

    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;

    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const isCodeExpired = (device) => {
    if (!device?.setupCodeExpiresAt) return false;
    return new Date(device.setupCodeExpiresAt).getTime() - now.value <= 0;
  };

  const startPairingWatcher = (deviceId) => {
    if (pairingInterval) clearInterval(pairingInterval);

    pairingInterval = setInterval(async () => {
      await handleRefreshDevices(true);

      const updated = devices.value.find((d) => d._id === deviceId);
      if (!updated) {
        clearInterval(pairingInterval);
        pairingInterval = null;
        return;
      }

      if (updated.status === "active") {
        clearInterval(pairingInterval);
        pairingInterval = null;
        showToast("success", "Device successfully paired!");
        return;
      }

      if (isCodeExpired(updated)) {
        clearInterval(pairingInterval);
        pairingInterval = null;
        return;
      }
    }, 5000);
  };

  const startTimer = () => {
    stopTimer();

    timerInterval = setInterval(() => {
      now.value = Date.now();
    }, 1000);
  };

  const stopTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }
  };

  const startAutoRefresh = () => {
    stopAutoRefresh();

    autoRefreshInterval = setInterval(async () => {
      const hasActiveCountdown = devices.value.some((device) => {
        const notExpired =
          device.setupCodeExpiresAt &&
          new Date(device.setupCodeExpiresAt).getTime() > Date.now();

        return device.status === "pending" && notExpired;
      });

      if (!hasActiveCountdown) {
        stopAutoRefresh();
        return;
      }

      await handleRefreshDevices(true);
    }, 5000);
  };

  const stopAutoRefresh = () => {
    if (autoRefreshInterval) {
      clearInterval(autoRefreshInterval);
      autoRefreshInterval = null;
    }
  };

  const formatRelativeTime = (date) => {
    const diff = Date.now() - new Date(date).getTime();

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    return `${days} day${days > 1 ? "s" : ""} ago`;
  };

  onMounted(async () => {
    await handleRefreshDevices();
    startTimer();
    startAutoRefresh();
  });

  onActivated(async () => {
    startTimer();
    startAutoRefresh();
  });

  onDeactivated(() => {
    stopTimer();
    stopAutoRefresh();
  });

  onUnmounted(() => {
    stopTimer();
    stopAutoRefresh();

    if (pairingInterval) {
      clearInterval(pairingInterval);
      pairingInterval = null;
    }
  });
</script>

<style scoped>
  .fade-scale-enter-active,
  .fade-scale-leave-active {
    transition: all 0.15s ease-out;
  }

  .fade-scale-enter-from,
  .fade-scale-leave-to {
    opacity: 0;
    transform: scale(0.95) translateY(-4px);
  }
</style>
